/* eslint-disable @typescript-eslint/naming-convention */
import React, { useMemo, useCallback } from 'react';
// until we dont split the packages for v2 we still import tooltip from ds-tooltip
import { DSTooltipV3 } from '@elliemae/ds-tooltip';
import type { DSGridT } from '@elliemae/ds-grid';

import {
  describe,
  useGetGlobalAttributes,
  useMemoMergePropsWithDefault,
  useGetXstyledProps,
} from '@elliemae/ds-props-helpers';
import { uid } from 'uid';
import type { DSCircularIndeterminateIndicatorT } from './react-desc-prop-types.js';
import { CircularIndeterminateIndicatorPropTypes, defaultProps } from './react-desc-prop-types.js';
import {
  colorToHex,
  sizeToPx,
  sizeToTextSize,
  sizeToWeight,
  DSCircularIndeterminateIndicatorName,
} from './constants.js';
import { StyledCircle, StyledContainer, StyledPath, StyledRect, StyledSvg, StyledLabel } from './styled.js';
// eslint-disable-next-line react/jsx-no-useless-fragment
const EmptyComp: typeof DSTooltipV3 = (props) => <>{props.children}</>;

export const DSCircularIndeterminateIndicator: React.ComponentType<DSCircularIndeterminateIndicatorT.Props> = (
  props,
) => {
  const propsWithDefault = useMemoMergePropsWithDefault<DSCircularIndeterminateIndicatorT.InternalProps>(
    props,
    defaultProps,
  );
  const globalAttributes = useGetGlobalAttributes<
    DSCircularIndeterminateIndicatorT.InternalProps,
    HTMLDivElement,
    DSGridT.Props
  >(propsWithDefault);
  const xstyledAttributes = useGetXstyledProps(propsWithDefault);

  const { size, color, text, showText, withTooltip, inline, tooltipStartPlacementPreference } = propsWithDefault;

  const Wrapper: typeof DSTooltipV3 = useMemo(() => (withTooltip ? DSTooltipV3 : EmptyComp), [withTooltip]);

  const uniqueId = useMemo(() => uid(16), []);

  const circleRadius = useMemo(() => (sizeToPx[size] - Number.parseInt(sizeToWeight[size], 10)) / 2, [size]);

  const getOwnerProps = useCallback(() => propsWithDefault, [propsWithDefault]);
  const getOwnerPropsArguments = useCallback(() => ({}), []);

  return (
    <StyledContainer
      inline={inline}
      gutter="xs"
      justifyContent="center"
      role="status"
      aria-label={text || 'Loading...'}
      data-testid="ds-circular-indeterminate-indicator-root"
      {...globalAttributes}
      {...xstyledAttributes}
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
    >
      <Wrapper text={text} textAlign="center" startPlacementPreference={tooltipStartPlacementPreference}>
        <StyledSvg
          width={sizeToPx[size]}
          height={sizeToPx[size]}
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
          aria-hidden="true"
        >
          <defs>
            <clipPath id={`not-gradient-clip-${uniqueId}`}>
              <StyledRect x="0%" y="0%" width="52%" height="100%" />
            </clipPath>
            <linearGradient id={`gradient-color-${uniqueId}`} x1="0%" x2="0%" y1="0%" y2="100%">
              <stop offset="10%" stopColor={`${colorToHex[color]}00`} />
              <stop offset="90%" stopColor={`${colorToHex[color]}FF`} />
            </linearGradient>
          </defs>
          <StyledCircle
            cx="50%"
            cy="50%"
            r={circleRadius}
            strokeWidth={sizeToWeight[size]}
            stroke={colorToHex[color]}
            strokeLinecap="round"
            clipPath={`url(#not-gradient-clip-${uniqueId})`}
          />
          {/* 
            Here we are using a path because:
            - Flexible enough to build a circle
            - When rotating, the gradient follows the rotation!!! (This is key)
            - We can use the rotation with keyframes, allowing for custom timing
            
            Using a circle does not pass point 2.
            Animating the gradient is not posible with custom timings.
            This is the best solution without using a custom library.
          */}
          <StyledPath
            d={`M ${sizeToPx[size] / 2} ${sizeToPx[size] / 2}
                m ${circleRadius}, 0
                a ${circleRadius}, ${circleRadius} 0 0, 1 -${circleRadius}, ${circleRadius}`}
            strokeWidth={sizeToWeight[size]}
            fill="transparent"
            stroke={`url(#gradient-color-${uniqueId})`}
            strokeLinecap="round"
            r={circleRadius}
          />
        </StyledSvg>
        {text !== '' && showText && (
          <StyledLabel
            style={{ color: color === 'light' ? '#FFFFFF' : '#353C46', fontSize: sizeToTextSize[size] }}
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
          >
            {text}
          </StyledLabel>
        )}
      </Wrapper>
    </StyledContainer>
  );
};

DSCircularIndeterminateIndicator.displayName = DSCircularIndeterminateIndicatorName;
export const DSCircularIndeterminateIndicatorWithSchema = describe(DSCircularIndeterminateIndicator);
DSCircularIndeterminateIndicatorWithSchema.propTypes = CircularIndeterminateIndicatorPropTypes;
